// crewbit-admin/src/components/money/StatusHero.tsx
//
// Spec: §InvoiceDetail.7.2 hero region. Status label + total amount + tone-driven
// subtitle. Mirrors mobile StatusHero (Session 124).

import { palette, urgency, spacing, typography } from '../../styles/tokens';
import { formatMoney } from '../../lib/money';
import type { Invoice } from '../../types';

type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

interface StatusConfig {
  label: string;
  tone: Tone;
  subtitle: (i: Invoice) => string;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtRelativeDays(iso: string | null | undefined): number {
  if (!iso) return 0;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / 86400000);
}

const STATUS_CONFIG: Record<Invoice['status'], StatusConfig> = {
  draft: {
    label: 'Draft',
    tone: 'neutral',
    subtitle: () => 'Not yet sent.',
  },
  sent: {
    label: 'Sent',
    tone: 'info',
    subtitle: (i) => i.due_date ? `Due ${fmtDate(i.due_date)}` : 'Awaiting payment.',
  },
  viewed: {
    label: 'Viewed',
    tone: 'info',
    subtitle: (i) => i.due_date ? `Due ${fmtDate(i.due_date)}` : 'Client opened the invoice.',
  },
  overdue: {
    label: 'Overdue',
    tone: 'warning',
    subtitle: (i) => {
      const days = i.due_date ? fmtRelativeDays(i.due_date) : 0;
      return days > 0 ? `${days} day${days === 1 ? '' : 's'} past due` : 'Past due.';
    },
  },
  paid: {
    label: 'Paid',
    tone: 'success',
    subtitle: (i) => i.paid_at ? `Paid ${fmtDate(i.paid_at)}` : 'Paid.',
  },
  partially_refunded: {
    label: 'Partially refunded',
    tone: 'warning',
    subtitle: (i) => i.refund_amount ? `${formatMoney(i.refund_amount)} refunded so far` : 'Partial refund issued.',
  },
  refunded: {
    label: 'Refunded',
    tone: 'neutral',
    subtitle: (i) => i.refunded_at ? `Refunded ${fmtDate(i.refunded_at)}` : 'Fully refunded.',
  },
  cancelled: {
    label: 'Cancelled',
    tone: 'neutral',
    subtitle: (i) => i.cancelled_at ? `Cancelled ${fmtDate(i.cancelled_at)}` : 'Cancelled.',
  },
  written_off: {
    label: 'Written off',
    tone: 'neutral',
    subtitle: (i) => i.written_off_at ? `Written off ${fmtDate(i.written_off_at)}` : 'Written off.',
  },
};

const TONE_COLOR: Record<Tone, { bg: string; fg: string }> = {
  neutral: { bg: palette.g100, fg: palette.g600 },
  info: { bg: palette.accentWash, fg: palette.accent },
  success: { bg: palette.greenWash, fg: palette.green },
  warning: { bg: palette.amberWash, fg: urgency.warning },
  danger: { bg: palette.redWash, fg: palette.red },
};

interface Props {
  invoice: Invoice;
}

export function StatusHero({ invoice }: Props) {
  const cfg = STATUS_CONFIG[invoice.status];
  const tone = TONE_COLOR[cfg.tone];
  const docLabel = invoice.is_sales_receipt ? 'Sales receipt' : 'Invoice';

  return (
    <div style={{ marginBottom: spacing.lg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
        <span
          style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '4px 10px', borderRadius: 999,
            background: tone.bg, color: tone.fg,
            fontSize: typography.size.micro, fontWeight: typography.weight.semibold,
            letterSpacing: 0.2, textTransform: 'uppercase',
          }}
        >
          {cfg.label}
        </span>
        <span style={{ fontSize: typography.size.caption, color: palette.g500 }}>
          {docLabel} · {invoice.invoice_number}
        </span>
      </div>

      <div
        style={{
          fontSize: typography.size.hero,
          fontWeight: typography.weight.bold,
          color: palette.g800,
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
        }}
      >
        {formatMoney(invoice.total)}
      </div>

      <div style={{ fontSize: typography.size.caption, color: palette.g500, marginTop: spacing.xs }}>
        {cfg.subtitle(invoice)}
      </div>
    </div>
  );
}
